/**
 * IndustryInsightsPanel — three collapsible cards rendered below the
 * IndustryData editor in Step 2 of the case wizard.
 *
 * Sections:
 *   1. Opportunity & Risk — opportunityAreas, riskAreas, strategicImperatives,
 *      opportunityScore, riskScore
 *   2. Disruptions — activeDisruptions, upcomingRegulations, geopoliticalRisks,
 *      supplyChainRisks, sustainabilityPressures
 *   3. Technology to Watch — technologiesToWatch, emergingTechnologies,
 *      keyInnovators, rdSpendTrend
 *
 * Each section that's empty shows an inline "Refresh Industry Research →"
 * link to /industry/{industryId} (same tab).
 *
 * Does NOT mutate the wizard store — purely display-only enrichment.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Target,
  AlertTriangle,
  Cpu,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import Card from '../ui/Card';
import { api } from '../../lib/api';

interface IndustryInsightsPanelProps {
  industryId: string | null | undefined;
}

interface IndustryInsightsResponse {
  industryId: string;
  industry?: {
    industryId?: string;
    name?: string;
    versionString?: string;
  } | null;
  industryInsights?: {
    executiveSummary?: {
      opportunityScore?: number | null;
      riskScore?: number | null;
      opportunityAreas?: string[];
      riskAreas?: string[];
      strategicImperatives?: string[];
      criticalSuccessFactors?: string[];
      companiesToWatch?: string[];
      technologiesToWatch?: string[];
      trendsToWatch?: string[];
      nearTermOutlook?: string | null;
      mediumTermOutlook?: string | null;
      longTermOutlook?: string | null;
    } | null;
    disruptions?: {
      activeDisruptions?: any[];
      upcomingRegulations?: string[];
      geopoliticalRisks?: string[];
      supplyChainRisks?: string[];
      sustainabilityPressures?: string[];
      consumerPreferenceShifts?: string[];
      regulatoryChanges?: string[];
    } | null;
    technologyTrends?: {
      technologiesToWatch?: string[];
      emergingTechnologies?: string[];
      keyInnovators?: string[];
      rdSpendTrend?: string | null;
      currentTrends?: any[];
      emergingTrends?: any[];
    } | null;
  } | null;
}

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  isEmpty?: boolean;
  emptyHint?: string;
  enrichUrl?: string;
  enrichLabel?: string;
  children?: React.ReactNode;
}> = ({ title, icon, defaultOpen = true, isEmpty, emptyHint, enrichUrl, enrichLabel, children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-neutral-900">{title}</span>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-neutral-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-neutral-500" />
        )}
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-neutral-100">
          {isEmpty ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-neutral-500">
                {emptyHint || 'Industry research doesn’t have this section yet.'}
              </p>
              {enrichUrl && (
                <button
                  onClick={() => navigate(enrichUrl)}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 flex-shrink-0"
                >
                  {enrichLabel || 'Refresh Industry Research'}
                  <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

const ChipList: React.FC<{ items?: string[]; tone?: 'opportunity' | 'risk' | 'neutral' }> = ({
  items,
  tone = 'neutral',
}) => {
  if (!items || items.length === 0) return null;
  const cls =
    tone === 'opportunity'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : tone === 'risk'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-neutral-50 text-neutral-700 border-neutral-200';
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, idx) => (
        <span
          key={idx}
          className={`px-2 py-0.5 text-xs rounded-full border ${cls}`}
        >
          {item}
        </span>
      ))}
    </div>
  );
};

const IndustryInsightsPanel: React.FC<IndustryInsightsPanelProps> = ({ industryId }) => {
  const enrichUrl = industryId ? `/industry/${industryId}` : undefined;

  const { data, isLoading } = useQuery<IndustryInsightsResponse>({
    queryKey: ['case-context', 'industry-insights', industryId],
    queryFn: () => api.get(`/case-discovery/industry-insights/${industryId}`),
    enabled: !!industryId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  if (!industryId) return null;
  if (isLoading) {
    return (
      <Card className="p-4 mt-4 flex items-center gap-2 text-neutral-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading industry insights…</span>
      </Card>
    );
  }

  const insights = data?.industryInsights ?? {};
  const exec = insights.executiveSummary ?? {};
  const disr = insights.disruptions ?? {};
  const tech = insights.technologyTrends ?? {};

  const execEmpty =
    !exec.opportunityAreas?.length &&
    !exec.riskAreas?.length &&
    !exec.strategicImperatives?.length &&
    exec.opportunityScore == null &&
    exec.riskScore == null;

  const disrEmpty =
    !disr.activeDisruptions?.length &&
    !disr.upcomingRegulations?.length &&
    !disr.geopoliticalRisks?.length &&
    !disr.supplyChainRisks?.length &&
    !disr.sustainabilityPressures?.length;

  const techEmpty =
    !tech.technologiesToWatch?.length &&
    !tech.emergingTechnologies?.length &&
    !tech.keyInnovators?.length &&
    !tech.rdSpendTrend &&
    !tech.currentTrends?.length;

  return (
    <Card className="p-5 mt-4">
      <h3 className="text-lg font-semibold text-neutral-900 mb-3">Industry Insights</h3>
      <p className="text-sm text-neutral-600 mb-4">
        Surfaced from comprehensive Industry Research — use these signals to inform
        strategic questions in the next step.
      </p>

      <div className="space-y-3">
        {/* Opportunity & Risk */}
        <Section
          title="Opportunity & Risk"
          icon={<Target className="w-4 h-4 text-emerald-600" />}
          isEmpty={execEmpty}
          enrichUrl={enrichUrl}
          enrichLabel="Refresh Industry Research"
        >
          <div className="space-y-3">
            {(exec.opportunityScore != null || exec.riskScore != null) && (
              <div className="flex gap-6 text-sm">
                {exec.opportunityScore != null && (
                  <div>
                    <span className="text-neutral-500">Opportunity score: </span>
                    <span className="font-medium text-emerald-700">
                      {(exec.opportunityScore * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
                {exec.riskScore != null && (
                  <div>
                    <span className="text-neutral-500">Risk score: </span>
                    <span className="font-medium text-red-700">
                      {(exec.riskScore * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            )}
            {exec.opportunityAreas && exec.opportunityAreas.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Opportunity Areas</p>
                <ChipList items={exec.opportunityAreas} tone="opportunity" />
              </div>
            )}
            {exec.riskAreas && exec.riskAreas.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Risk Areas</p>
                <ChipList items={exec.riskAreas} tone="risk" />
              </div>
            )}
            {exec.strategicImperatives && exec.strategicImperatives.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Strategic Imperatives</p>
                <ChipList items={exec.strategicImperatives} />
              </div>
            )}
          </div>
        </Section>

        {/* Disruptions */}
        <Section
          title="Disruptions"
          icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
          isEmpty={disrEmpty}
          enrichUrl={enrichUrl}
        >
          <div className="space-y-3">
            {disr.activeDisruptions && disr.activeDisruptions.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Active Disruptions</p>
                <ChipList
                  items={disr.activeDisruptions
                    .map((d: any) => d?.name || d?.title || (typeof d === 'string' ? d : null))
                    .filter(Boolean) as string[]}
                  tone="risk"
                />
              </div>
            )}
            {disr.upcomingRegulations && disr.upcomingRegulations.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Upcoming Regulations</p>
                <ChipList items={disr.upcomingRegulations} />
              </div>
            )}
            {disr.geopoliticalRisks && disr.geopoliticalRisks.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Geopolitical Risks</p>
                <ChipList items={disr.geopoliticalRisks} tone="risk" />
              </div>
            )}
            {disr.supplyChainRisks && disr.supplyChainRisks.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Supply Chain Risks</p>
                <ChipList items={disr.supplyChainRisks} tone="risk" />
              </div>
            )}
            {disr.sustainabilityPressures && disr.sustainabilityPressures.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Sustainability Pressures</p>
                <ChipList items={disr.sustainabilityPressures} />
              </div>
            )}
          </div>
        </Section>

        {/* Technology to Watch */}
        <Section
          title="Technology to Watch"
          icon={<Cpu className="w-4 h-4 text-primary-600" />}
          isEmpty={techEmpty}
          enrichUrl={enrichUrl}
        >
          <div className="space-y-3">
            {tech.technologiesToWatch && tech.technologiesToWatch.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Technologies to Watch</p>
                <ChipList items={tech.technologiesToWatch} />
              </div>
            )}
            {tech.emergingTechnologies && tech.emergingTechnologies.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Emerging Technologies</p>
                <ChipList items={tech.emergingTechnologies} />
              </div>
            )}
            {tech.keyInnovators && tech.keyInnovators.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1.5">Key Innovators</p>
                <ChipList items={tech.keyInnovators} />
              </div>
            )}
            {tech.rdSpendTrend && (
              <div className="text-sm">
                <span className="text-neutral-500">R&amp;D spend trend: </span>
                <span className="font-medium text-neutral-900">{tech.rdSpendTrend}</span>
              </div>
            )}
          </div>
        </Section>
      </div>
    </Card>
  );
};

export default IndustryInsightsPanel;
